<template>
  <div>
    <storage-usage-modal
      :visible.sync="storageModalVisible"
      :storage-limit-bytes="packStorageLimitBytes"
    />

    <a-dropdown
      :trigger="['click']"
      placement="bottomRight"
      overlay-class-name="cui-user-menu-overlay"
      @visibleChange="addCount"
    >
      <a-tooltip
        placement="bottom"
        :mouse-enter-delay="0.35"
        :title="storageQuotaExceededTooltip"
        overlay-class-name="cui-user-menu-storage-tooltip"
      >
        <div
          :class="[
            $style.dropdown,
            storageQuotaExceededBadge ? $style.dropdownWithQuotaBadge : null,
          ]"
        >
          <a-badge>
            <span
              v-if="storageQuotaExceededBadge"
              slot="count"
              :class="$style.badgeStorageExceeded"
              role="img"
              :aria-label="storageExceededAriaLabel"
            >
              <a-icon type="warning" theme="filled" />
            </span>
            <a-avatar
              v-if="!avatar"
              shape="square"
              icon="user"
              size="large"
              :class="$style.avatar"
            />
            <a-avatar
              v-else
              shape="square"
              :src="user.avatar"
              size="large"
              :class="$style.avatar"
            />
          </a-badge>
        </div>
      </a-tooltip>
      <a-menu slot="overlay" :class="$style.menuRoot">
        <a-menu-item :class="$style.menuItemFlush">
          <div :class="$style.headerBlock">
            <div :class="$style.greeting">
              {{ $t("topBar.profileMenu.hello") }},
              <span :class="$style.greetingName">{{
                firstName || "Anonyme"
              }}</span>
            </div>
            <div :class="$style.metaRow">
              <span :class="$style.metaLabel">{{
                $t("topBar.profileMenu.role")
              }}</span>
              <span :class="$style.metaValue">{{ user.role || "—" }}</span>
            </div>
            <div :class="$style.metaRow">
              <span :class="$style.metaLabel">{{ $t("topBar.pack") }}</span>
              <span :class="$style.packPill">{{
                (schoolDetails.pack && schoolDetails.pack.name) || "—"
              }}</span>
            </div>
          </div>
        </a-menu-item>

        <a-menu-item
          v-if="settings.activeBuilding"
          :class="[$style.menuItemFlush, $style.menuItemClickable]"
          @click="openStorageModal"
        >
          <div :class="$style.storageCard">
            <div :class="$style.storageCardHeader">
              <div :class="$style.storageTitle">
                <span :class="$style.storageIconWrap">
                  <a-icon type="database" />
                </span>
                <span>{{ $t("topBar.storage.menuLabel") }}</span>
              </div>
              <div :class="$style.storageFigures">
                {{ storageMenuPrimary }}
              </div>
            </div>
            <a-progress
              v-if="
                hasStorageLimit && latestScan && latestScan.totalBytes != null
              "
              :percent="menuBarPercent"
              :status="menuBarStatus"
              :show-info="false"
              :stroke-width="6"
              :class="$style.storageProgress"
            />
            <div v-else :class="$style.storageHint">
              {{ $t("topBar.storage.menuHintNoBar") }}
            </div>
          </div>
        </a-menu-item>

        <a-menu-item :class="$style.menuItemFlush">
          <div :class="$style.upgradeWrap">
            <a-button
              type="primary"
              block
              :class="$style.upgradeBtn"
              @click="showUpgradeModal"
            >
              {{ $t("pack.upgrade") }}
              <a-icon type="up-circle" class="ml-2" />
            </a-button>
          </div>
        </a-menu-item>

        <a-menu-divider :class="$style.menuDivider" />

        <a-menu-item :class="$style.menuItemAction">
          <router-link :class="$style.actionLink" to="/profile">
            <a-icon type="user" :class="$style.actionIcon" />
            {{ $t("menu.profile") }}
          </router-link>
        </a-menu-item>
        <a-menu-item :class="$style.menuItemAction">
          <a
            href="javascript: void(0);"
            :class="$style.actionLink"
            @click="logout"
          >
            <a-icon type="logout" :class="$style.actionIcon" />
            {{ $t("menu.logout") }}
          </a>
        </a-menu-item>
      </a-menu>
    </a-dropdown>
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions, mapMutations } from "vuex";
import store from "store";
import apiClient from "@/services/axios";
import {
  getPackStorageLimitBytes,
  clampedBarPercent as computeClampedBarPercent,
  storageProgressStatus,
} from "@/utilities/storageQuota";
import StorageUsageModal from "./components/StorageUsageModal.vue";

export default {
  components: {
    StorageUsageModal,
  },
  computed: {
    ...mapState(["user", "settings"]),
    schoolDetails() {
      return (
        this.user.building.filter(
          (el) => el.dbName == this.settings.activeBuilding
        )[0] || {}
      );
    },
    loading() {
      return this.$store.state.user.loading;
    },
    packStorageLimitBytes() {
      return getPackStorageLimitBytes(this.schoolDetails.pack);
    },
    hasStorageLimit() {
      return (
        this.packStorageLimitBytes != null && this.packStorageLimitBytes > 0
      );
    },
    storageMenuPrimary() {
      if (!this.latestScan || this.latestScan.totalBytes == null) {
        return "—";
      }
      const used = this.formatBytes(this.latestScan.totalBytes);
      if (!this.hasStorageLimit) return used;
      return `${used} / ${this.formatBytes(this.packStorageLimitBytes)}`;
    },
    menuBarPercent() {
      if (
        !this.hasStorageLimit ||
        !this.latestScan ||
        this.latestScan.totalBytes == null
      ) {
        return 0;
      }
      return computeClampedBarPercent(
        this.latestScan.totalBytes,
        this.packStorageLimitBytes
      );
    },
    menuBarStatus() {
      if (
        !this.hasStorageLimit ||
        !this.latestScan ||
        this.latestScan.totalBytes == null
      ) {
        return "normal";
      }
      return storageProgressStatus(
        this.latestScan.totalBytes,
        this.packStorageLimitBytes
      );
    },
    /** True when usage is at or over pack limit (same idea as server STORAGE_QUOTA_EXCEEDED). */
    storageQuotaExceeded() {
      if (!this.hasStorageLimit || !this.latestScan) return false;
      const used = this.latestScan.totalBytes;
      if (used == null || Number.isNaN(Number(used))) return false;
      return Number(used) >= this.packStorageLimitBytes;
    },
    /** Avatar badge: only when quota is exceeded for the active school. */
    storageQuotaExceededBadge() {
      return (
        !!this.settings.activeBuilding && this.storageQuotaExceeded
      );
    },
    /** Tooltip only when the warning badge is shown. */
    storageQuotaExceededTooltip() {
      if (!this.storageQuotaExceededBadge) return undefined;
      return this.$t("topBar.storage.quotaOver");
    },
    storageExceededAriaLabel() {
      if (!this.storageQuotaExceededBadge) return "";
      return this.$t("topBar.storage.quotaOver");
    },
  },
  data: function () {
    return {
      count: 7,
      firstName: undefined,
      avatar: undefined,
      latestScan: null,
      storageModalVisible: false,
    };
  },
  methods: {
    ...mapMutations("settings", ["showUpgradeModal"]),

    ...mapActions({
      userLogout: "user/LOGOUT",
    }),
    formatBytes(bytes) {
      if (bytes == null || Number.isNaN(Number(bytes))) return "—";
      const n = Number(bytes);
      const units = ["B", "KB", "MB", "GB", "TB"];
      let i = 0;
      let v = n;
      while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i++;
      }
      const decimals = i === 0 ? 0 : v < 10 ? 2 : 1;
      return `${v.toFixed(decimals)} ${units[i]}`;
    },
    async fetchLatestScan() {
      if (!this.settings.activeBuilding) {
        this.latestScan = null;
        return;
      }
      try {
        const { data } = await apiClient.get("/dbUsageScans/latest");
        this.latestScan = data;
      } catch (e) {
        this.latestScan = null;
      }
    },
    openStorageModal() {
      this.storageModalVisible = true;
    },
    async logout() {
      this.$socket.client.emit("adminLeave");
      this.$socket.client.removeAllListeners();
      store.set("app.topbar.favs", "");
      this.userLogout();
    },
    addCount() {
      this.count++;
    },
  },
  sockets: {
    desactivation(data) {
      if (data.userName === this.user.userName) {
        this.$notification.open({
          message: this.$t("notifications.desactivation"),
          icon: <i class="fa fa-exclamation-triangle text-danger" />,
        });
        this.userLogout();
      }
    },
    desactivateBuilding(data) {
      if (data === this.settings.activeBuildingId) {
        this.$notification.open({
          message: this.$t("notifications.desactivateBuilding"),
          icon: <i class="fa fa-exclamation-triangle text-danger" />,
        });
        this.userLogout();
      }
    },
    updateuser(data) {
      if (data.userName === this.user.userName) {
        this.$notification.open({
          message: this.$t("notifications.updateuser"),
          icon: <i class="fa fa-exclamation text-warning" />,
        });
        this.userLogout();
      }
    },
    updateAccess(data) {
      if (data.accessId === this.user.roleId) {
        this.$notification.open({
          message: this.$t("notifications.updateAccess"),
          icon: <i class="fa fa-exclamation text-warning" />,
        });
        this.userLogout();
      }
    },
  },
  watch: {
    "user.avatar": {
      handler(newAvatar) {
        this.avatar = newAvatar;
      },
      immediate: true,
    },
    "settings.activeBuilding"() {
      this.fetchLatestScan();
    },
  },
  async mounted() {
    if (this.user.avatar) this.avatar = this.user.avatar;
    if (this.user) {
      await apiClient.get("/users/" + this.user.userName).then((result) => {
        const data = result.data;
        this.firstName = data.userName;
        this.avatar = data.avatar;
      });
    }
    this.fetchLatestScan();
  },
};
</script>

<style lang="scss" module>
@import "./style.module.scss";
</style>

<style lang="scss">
/* Dropdown panel is portaled — overlay-class-name applies to the overlay root */
.cui-user-menu-overlay {
  .ant-dropdown-menu {
    padding: 6px 0 !important;
    border-radius: 12px !important;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    box-shadow: 0 10px 40px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06) !important;
  }
}

[data-kit-theme="dark"] .cui-user-menu-overlay .ant-dropdown-menu {
  border-color: rgba(255, 255, 255, 0.1) !important;
  background: #1a1a1a !important;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.45) !important;
}

/* Tooltip copy can be long (quota text); keep readable width */
.cui-user-menu-storage-tooltip.ant-tooltip {
  max-width: min(320px, 92vw);
}

.cui-user-menu-storage-tooltip .ant-tooltip-inner {
  text-align: left;
  line-height: 1.45;
  padding: 10px 12px;
}
</style>
